
import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Search, ArrowLeft, ArrowRight, RotateCw, Shuffle, Eye, EyeOff, BookOpen, Layers, CornerDownRight, PenTool, Eraser, Trash2, ChevronUp, ChevronDown, Circle, Star } from 'lucide-react';
import { Question, Folder } from '../data/types';
import { getTags } from '../utils';

// --- IndexedDB Helper Utilities ---
// (Same as before)
const DB_NAME = 'MathQuizDrawingsDB';
const DB_VERSION = 1;
const STORE_NAME = 'drawings';

const initDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const saveDrawingToDB = async (key: string, blob: Blob) => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.put(blob, key);
    } catch (err) {
        console.error("Error saving to DB", err);
    }
};

const getDrawingFromDB = async (key: string): Promise<Blob | null> => {
    try {
        const db = await initDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => resolve(null);
        });
    } catch (err) {
        return null;
    }
};

// --- Color Helpers ---
const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
};

// SVG Cursor Generator
const getEraserCursor = (size: number) => {
    const cursorSize = size * 2 + 2; 
    const center = cursorSize / 2;
    const svg = `
        <svg width="${cursorSize}" height="${cursorSize}" viewBox="0 0 ${cursorSize} ${cursorSize}" xmlns="http://www.w3.org/2000/svg">
            <circle cx="${center}" cy="${center}" r="${size}" fill="rgba(255, 255, 255, 0.3)" stroke="black" stroke-width="1.5"/>
            <circle cx="${center}" cy="${center}" r="${1}" fill="black" />
        </svg>
    `;
    const encoded = encodeURIComponent(svg);
    return `url('data:image/svg+xml;charset=utf-8,${encoded}') ${center} ${center}, auto`;
};

// Eraser Sizes (Radius)
const ERASER_SIZES = [8, 16, 32]; 

export const CardBrowserView = ({ folders, questions, favoriteQuestionIds, toggleFavorite, rootId }: { folders: Folder[], questions: Question[], favoriteQuestionIds: number[], toggleFavorite: (id: number) => void, rootId: string }) => {
    // Mode: 'select' (filter questions) or 'browse' (view cards)
    const [mode, setMode] = useState<'select' | 'browse'>('select');
    
    // Selection State
    // Default selected folder logic handled in select element value logic
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [tagSearch, setTagSearch] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    
    // Browse State
    const [deck, setDeck] = useState<Question[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);

    // Drawing / Scratchpad State
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isPaperOpen, setIsPaperOpen] = useState(false);
    const [currentTool, setCurrentTool] = useState<'pen' | 'eraser'>('pen');
    const [penColor, setPenColor] = useState('#EF4444'); // Default Red
    const [brushSize, setBrushSize] = useState(3);
    const [eraserSizeIndex, setEraserSizeIndex] = useState(1); // Default Medium
    
    // Canvas Refs & Storage
    const mainCanvasRef = useRef<HTMLCanvasElement>(null);
    const mainContainerRef = useRef<HTMLDivElement>(null);
    
    const paperCanvasRef = useRef<HTMLCanvasElement>(null);
    const paperContainerRef = useRef<HTMLDivElement>(null);

    const memoryCache = useRef<Record<string, HTMLImageElement>>({});

    // Helpers for Selection
    const getQuestionsInFolderRecursive = (folderId: string | null): Question[] => {
        let result = questions.filter((q: Question) => (q.folderId || null) === (folderId || null));
        const subFolders = folders.filter((f: Folder) => (f.parentId || null) === (folderId || null));
        subFolders.forEach((sub: Folder) => {
            result = [...result, ...getQuestionsInFolderRecursive(sub.id)];
        });
        return result;
    };

    const activeRootId = rootId || 'grade-private';

    const questionsInScope = (selectedFolderId === activeRootId || selectedFolderId === null)
        ? getQuestionsInFolderRecursive(activeRootId)
        : getQuestionsInFolderRecursive(selectedFolderId);
    
    const availableCategories: string[] = Array.from(new Set(
        questionsInScope.flatMap((q: Question) => getTags(q.category))
    )).sort() as string[];

    const filteredCategories = availableCategories.filter(cat => 
        cat.toLowerCase().includes(tagSearch.toLowerCase())
    );

    const toggleCategory = (cat: string) => {
        setSelectedCategories(prev => 
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    // Filter available subjects based on rootId
    const subjectFolders = folders.filter((f: Folder) => f.parentId === activeRootId);
    const totalGradeQuestions = getQuestionsInFolderRecursive(activeRootId).length;

    // Start Browsing
    const startBrowsing = (shuffle: boolean = false) => {
        let qList = [...questionsInScope];
        if (selectedCategories.length > 0) {
            qList = qList.filter(q => {
                const qTags = getTags(q.category);
                return selectedCategories.some(selectedCat => qTags.includes(selectedCat));
            });
        }

        if (qList.length === 0) {
            alert("此範圍內沒有題目！");
            return;
        }

        if (shuffle) {
            qList = qList.sort(() => Math.random() - 0.5);
        }

        setDeck(qList);
        setCurrentIndex(0);
        setIsFlipped(false);
        setIsDrawingMode(false);
        setIsPaperOpen(false);
        setMode('browse');
    };

    // --- Drawing Logic Start (Same as before) ---
    const updateMemoryCache = (layer: 'main' | 'paper') => {
        if (!deck[currentIndex]) return;
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        if (!canvas) return;
        const dataUrl = canvas.toDataURL('image/webp', 0.8);
        const img = new Image();
        img.src = dataUrl;
        const key = `${deck[currentIndex].id}_${layer}`;
        memoryCache.current[key] = img;
    };

    const persistCurrentToDB = () => {
        if (!deck[currentIndex]) return;
        const currentQId = deck[currentIndex].id;
        const saveLayer = (layer: 'main' | 'paper') => {
             const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
             if (!canvas) return;
             canvas.toBlob((blob) => {
                if (blob) { saveDrawingToDB(`${currentQId}_${layer}`, blob); }
             }, 'image/webp', 0.8);
        };
        saveLayer('main'); saveLayer('paper');
    };

    const loadCanvasLayer = async (layer: 'main' | 'paper', qIndex: number = currentIndex) => {
        if (!deck[qIndex]) return;
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d', { willReadFrequently: true });
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const key = `${deck[qIndex].id}_${layer}`;
        if (memoryCache.current[key]) {
            ctx.drawImage(memoryCache.current[key], 0, 0);
            return;
        }
        const blob = await getDrawingFromDB(key);
        if (blob) {
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                if (deck[currentIndex].id === deck[qIndex].id) {
                    ctx.drawImage(img, 0, 0);
                    memoryCache.current[key] = img;
                }
                URL.revokeObjectURL(url);
            };
            img.src = url;
        }
    };

    const loadAllDrawings = () => { loadCanvasLayer('main'); loadCanvasLayer('paper'); };

    const initCanvasSize = (canvas: HTMLCanvasElement | null, container: HTMLDivElement | null, layer: 'main' | 'paper') => {
        if (canvas && container) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
            loadCanvasLayer(layer);
        }
    };

    useEffect(() => {
        if (mode === 'browse') {
            const handleResize = () => {
                initCanvasSize(mainCanvasRef.current, mainContainerRef.current, 'main');
                initCanvasSize(paperCanvasRef.current, paperContainerRef.current, 'paper');
            };
            window.addEventListener('resize', handleResize);
            handleResize();
            const timeoutId = setTimeout(handleResize, 100);
            return () => { window.removeEventListener('resize', handleResize); clearTimeout(timeoutId); };
        }
    }, [mode, isPaperOpen]);

    useEffect(() => { if (mode === 'browse') { loadAllDrawings(); } }, [currentIndex, mode]);

    useEffect(() => {
        if (mode === 'browse') {
            const handleVisibilityChange = () => { if (document.visibilityState === 'hidden') { persistCurrentToDB(); } };
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    }, [mode, currentIndex, deck]);

    const eraseSpecificColor = (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, targetRgb: {r: number, g: number, b: number}) => {
        const startX = Math.floor(Math.max(0, x - radius));
        const startY = Math.floor(Math.max(0, y - radius));
        const endX = Math.ceil(Math.min(ctx.canvas.width, x + radius));
        const endY = Math.ceil(Math.min(ctx.canvas.height, y + radius));
        const w = endX - startX;
        const h = endY - startY;
        if (w <= 0 || h <= 0) return;
        const imageData = ctx.getImageData(startX, startY, w, h);
        const data = imageData.data;
        const rSq = radius * radius;
        for (let row = 0; row < h; row++) {
            for (let col = 0; col < w; col++) {
                const dx = col + startX - x;
                const dy = row + startY - y;
                if (dx * dx + dy * dy <= rSq) {
                    const i = (row * w + col) * 4;
                    const r = data[i]; const g = data[i + 1]; const b = data[i + 2]; const a = data[i + 3];
                    if (a > 0) {
                        const dist = (r - targetRgb.r) ** 2 + (g - targetRgb.g) ** 2 + (b - targetRgb.b) ** 2;
                        if (dist < 3500) { data[i + 3] = 0; }
                    }
                }
            }
        }
        ctx.putImageData(imageData, startX, startY);
    };

    const handleStartDrawing = (e: React.MouseEvent | React.TouchEvent, layer: 'main' | 'paper') => {
        if (!isDrawingMode) return;
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d', { willReadFrequently: true });
        if (!canvas || !ctx) return;
        const getPos = (evt: React.MouseEvent | React.TouchEvent) => {
            const rect = canvas.getBoundingClientRect();
            if ('touches' in evt) { return { x: evt.touches[0].clientX - rect.left, y: evt.touches[0].clientY - rect.top }; }
            return { x: (evt as React.MouseEvent).clientX - rect.left, y: (evt as React.MouseEvent).clientY - rect.top };
        };
        const startPos = getPos(e);
        let lastX = startPos.x; let lastY = startPos.y;
        const targetRgb = currentTool === 'eraser' ? hexToRgb(penColor) : null;
        const eraserRadius = ERASER_SIZES[eraserSizeIndex];
        if (currentTool === 'pen') {
            ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = brushSize; ctx.strokeStyle = penColor; ctx.globalCompositeOperation = 'source-over';
        } else { if (targetRgb) eraseSpecificColor(ctx, lastX, lastY, eraserRadius, targetRgb); }
        const draw = (moveEvent: MouseEvent | TouchEvent) => {
            moveEvent.preventDefault();
            const pos = getPos(moveEvent as any);
            if (currentTool === 'pen') { ctx.lineTo(pos.x, pos.y); ctx.stroke(); }
            else if (currentTool === 'eraser' && targetRgb) {
                const dist = Math.hypot(pos.x - lastX, pos.y - lastY);
                const steps = Math.ceil(dist / (eraserRadius / 2)); 
                for (let i = 1; i <= steps; i++) {
                    const ix = lastX + (pos.x - lastX) * (i / steps);
                    const iy = lastY + (pos.y - lastY) * (i / steps);
                    eraseSpecificColor(ctx, ix, iy, eraserRadius, targetRgb);
                }
            }
            lastX = pos.x; lastY = pos.y;
        };
        const stop = () => {
            if (currentTool === 'pen') ctx.closePath();
            updateMemoryCache(layer);
            canvas.removeEventListener('mousemove', draw); canvas.removeEventListener('mouseup', stop);
            canvas.removeEventListener('touchmove', draw); canvas.removeEventListener('touchend', stop);
        };
        canvas.addEventListener('mousemove', draw); canvas.addEventListener('mouseup', stop);
        canvas.addEventListener('touchmove', draw, { passive: false }); canvas.addEventListener('touchend', stop);
    };

    const clearActiveCanvas = () => {
        const layer = isPaperOpen ? 'paper' : 'main';
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const key = `${deck[currentIndex].id}_${layer}`;
            delete memoryCache.current[key];
            initDB().then(db => {
                const tx = db.transaction(STORE_NAME, 'readwrite');
                tx.objectStore(STORE_NAME).delete(key);
            });
        }
    };
    
    const handleEraserClick = () => { if (currentTool === 'eraser') { setEraserSizeIndex(prev => (prev + 1) % ERASER_SIZES.length); } else { setCurrentTool('eraser'); } };
    const dynamicCursor = isDrawingMode ? (currentTool === 'eraser' ? getEraserCursor(ERASER_SIZES[eraserSizeIndex]) : 'crosshair') : 'auto';
    // --- Drawing Logic End ---

    // Card Navigation
    const nextCard = () => { if (currentIndex < deck.length - 1) { persistCurrentToDB(); setIsFlipped(false); setTimeout(() => setCurrentIndex(prev => prev + 1), 150); } };
    const prevCard = () => { if (currentIndex > 0) { persistCurrentToDB(); setIsFlipped(false); setTimeout(() => setCurrentIndex(prev => prev - 1), 150); } };
    const handleExit = () => { persistCurrentToDB(); setMode('select'); };
    const handleKeyDown = (e: KeyboardEvent) => {
        if (isDrawingMode) return; 
        if (e.key === 'ArrowRight' || e.key === ' ') { if (!isFlipped) setIsFlipped(true); else nextCard(); } 
        else if (e.key === 'ArrowLeft') { prevCard(); }
    };
    useEffect(() => { if (mode === 'browse') { window.addEventListener('keydown', handleKeyDown); return () => window.removeEventListener('keydown', handleKeyDown); } }, [mode, isFlipped, currentIndex, isDrawingMode]);

    // RENDER: Selection View
    if (mode === 'select') {
        return (
            <div className="flex-1 bg-fudan-light overflow-y-auto p-4 flex items-center justify-center">
                <div className="bg-white max-w-2xl w-full p-8 rounded-2xl shadow-xl border-t-8 border-yellow-500 animate-fade-in-up">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full mb-4">
                            <Layers className="w-8 h-8" />
                        </div>
                        <h1 className="text-3xl font-serif font-bold text-gray-800 tracking-wide">
                            卡片瀏覽模式
                        </h1>
                        <p className="text-gray-500 mt-2">
                            沒有時間限制，點擊卡片翻看答案，適合複習與背誦。
                        </p>
                    </div>

                    {/* Folder Select */}
                    <div className="mb-6">
                        <label className="block text-gray-700 font-bold mb-2">步驟 1：選擇科目資料夾</label>
                        <div className="relative">
                            <FolderOpen className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                            <select 
                                className="w-full pl-10 p-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-yellow-400 outline-none appearance-none"
                                value={selectedFolderId || activeRootId}
                                onChange={(e) => {
                                    setSelectedFolderId(e.target.value);
                                    setSelectedCategories([]);
                                }}
                            >
                                <option value={activeRootId}>全部科目 (共 {totalGradeQuestions} 題)</option>
                                {subjectFolders.map((f: Folder) => {
                                    const count = getQuestionsInFolderRecursive(f.id).length;
                                    return (
                                        <option key={f.id} value={f.id}>
                                            📂 {f.name} (含子目錄約 {count} 題)
                                        </option>
                                    );
                                })}
                            </select>
                            <CornerDownRight className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Tag Filter */}
                    <div className="mb-8">
                        <label className="block text-gray-700 font-bold mb-2 flex justify-between">
                            <span>步驟 2：篩選標籤 (選填)</span>
                            <span className="text-xs text-gray-400 font-normal">已選: {selectedCategories.length} 個</span>
                        </label>
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            <input 
                                type="text"
                                placeholder="搜尋標籤..."
                                value={tagSearch}
                                onChange={(e) => setTagSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[80px] max-h-[150px] overflow-y-auto">
                            {filteredCategories.length === 0 && <span className="text-gray-400 text-sm">無符合標籤</span>}
                            {filteredCategories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => toggleCategory(cat)}
                                    className={`px-3 py-1 text-sm rounded-full border transition-all ${
                                        selectedCategories.includes(cat)
                                        ? 'bg-yellow-500 text-white border-yellow-500 shadow-sm'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => startBrowsing(false)}
                            className="flex items-center justify-center py-3 px-4 bg-white border-2 border-yellow-500 text-yellow-600 font-bold rounded-xl hover:bg-yellow-50 transition-colors"
                        >
                            <BookOpen className="w-5 h-5 mr-2" />
                            依序瀏覽
                        </button>
                        <button
                            onClick={() => startBrowsing(true)}
                            className="flex items-center justify-center py-3 px-4 bg-yellow-500 text-white font-bold rounded-xl hover:bg-yellow-600 shadow-md transition-transform transform hover:scale-105"
                        >
                            <Shuffle className="w-5 h-5 mr-2" />
                            隨機抽卡
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // RENDER: Browse View
    // ... [Card Browser View (Front/Back) - same as before] ...
    const currentQ = deck[currentIndex];
    const isFavorite = favoriteQuestionIds ? favoriteQuestionIds.includes(currentQ.id) : false;

    return (
        <div className="flex-1 bg-gray-100 flex flex-col h-full overflow-hidden relative" ref={mainContainerRef}>
            
            <canvas ref={mainCanvasRef} onMouseDown={(e) => handleStartDrawing(e, 'main')} onTouchStart={(e) => handleStartDrawing(e, 'main')} className={`absolute inset-0 z-30 touch-none ${isDrawingMode ? 'pointer-events-auto' : 'pointer-events-none'}`} style={{ cursor: dynamicCursor }} />

            <div ref={paperContainerRef} className={`fixed inset-x-0 bottom-0 bg-white z-40 border-t-2 border-gray-300 shadow-[0_-5px_15px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-in-out ${isPaperOpen ? 'translate-y-0' : 'translate-y-full'}`} style={{ height: '50%', backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                <div className="absolute top-2 right-2 text-xs text-gray-400 font-mono select-none z-50">計算紙</div>
                <canvas ref={paperCanvasRef} onMouseDown={(e) => handleStartDrawing(e, 'paper')} onTouchStart={(e) => handleStartDrawing(e, 'paper')} className={`absolute inset-0 z-40 touch-none ${isDrawingMode ? 'pointer-events-auto' : 'pointer-events-none'}`} style={{ cursor: dynamicCursor }} />
            </div>

            {isDrawingMode && (
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-full shadow-xl border border-gray-200 px-4 py-2 flex items-center space-x-4 animate-fade-in-up">
                    <div className="flex space-x-2 border-r border-gray-200 pr-4">{['#000000', '#EF4444', '#3B82F6', '#10B981'].map(color => (<button key={color} onClick={() => { setPenColor(color); setCurrentTool('pen'); }} className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${penColor === color && currentTool === 'pen' ? 'border-gray-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />))}</div>
                    <div className="relative flex flex-col items-center group"><button onClick={handleEraserClick} className={`p-2 rounded-full hover:bg-gray-100 relative ${currentTool === 'eraser' ? 'bg-gray-200 text-gray-800' : 'text-gray-500'}`} title="橡皮擦 (點擊切換大小)"><Eraser className="w-5 h-5" />{currentTool === 'eraser' && (<div className="absolute -top-1 -right-1 w-3 h-3 bg-fudan-blue rounded-full border border-white flex items-center justify-center"><span className="text-[8px] text-white font-bold">{['S','M','L'][eraserSizeIndex]}</span></div>)}</button></div>
                    <button onClick={clearActiveCanvas} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-red-500" title={isPaperOpen ? "清空計算紙" : "清空卡片塗鴉"}><Trash2 className="w-5 h-5" /></button>
                    <div className="w-px h-6 bg-gray-200"></div>
                    <button onClick={() => setIsPaperOpen(!isPaperOpen)} className={`flex items-center px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isPaperOpen ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{isPaperOpen ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronUp className="w-4 h-4 mr-1" />}計算紙</button>
                </div>
            )}

            <div className="bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center shadow-sm z-10 shrink-0">
                <button onClick={handleExit} className="text-gray-500 hover:text-gray-800 flex items-center font-bold"><ArrowLeft className="w-5 h-5 mr-1" /> 結束</button>
                <div className="text-gray-700 font-mono font-bold text-lg">{currentIndex + 1} / {deck.length}</div>
                <button onClick={() => { setIsFlipped(false); startBrowsing(true); }} className="text-gray-500 hover:text-yellow-600 flex items-center text-sm"><RotateCw className="w-4 h-4 mr-1" /> 重洗</button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col items-center justify-center relative z-0">
                <div className="relative w-full max-w-3xl perspective-1000 group cursor-pointer" onClick={() => !isDrawingMode && setIsFlipped(!isFlipped)}>
                    <div className={`relative w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden transition-all duration-500 transform ${isFlipped ? 'ring-4 ring-green-100' : ''} min-h-[400px] flex flex-col ${isPaperOpen ? 'mb-[40vh]' : ''}`}>
                        <div className={`h-2 w-full ${isFlipped ? 'bg-green-500' : 'bg-fudan-blue'}`} />
                        <button onClick={(e) => { e.stopPropagation(); toggleFavorite && toggleFavorite(currentQ.id); }} className="absolute top-4 right-4 z-20 p-2 rounded-full hover:bg-gray-100 transition-colors" title={isFavorite ? "取消收藏" : "加入最愛"}><Star className={`w-6 h-6 ${isFavorite ? 'text-orange-500 fill-current' : 'text-gray-300'}`} /></button>
                        <div className="flex-1 p-8 md:p-12 flex flex-col items-center justify-center text-center select-none">
                             <div className={`transition-opacity duration-300 ${isFlipped ? 'opacity-40 scale-95 blur-[1px]' : 'opacity-100'}`}>
                                <div className="flex flex-wrap gap-2 justify-center mb-6 pr-8">{getTags(currentQ.category).map(tag => (<span key={tag} className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded">{tag}</span>))}</div>
                                <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 leading-relaxed mb-6">{currentQ.text}</h2>
                                {currentQ.svg && (<div className="mb-6 flex justify-center">{currentQ.svg}</div>)}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full text-left">{currentQ.options.map((opt, idx) => (<div key={idx} className="p-3 border border-gray-200 rounded-lg text-gray-600"><span className="font-bold mr-2">{String.fromCharCode(65+idx)}.</span> {opt}</div>))}</div>
                             </div>
                             {isFlipped && (<div className="absolute inset-0 bg-white/95 z-10 flex flex-col items-center justify-center p-8 md:p-12 animate-fade-in"><div className="text-green-600 font-bold text-xl mb-4 flex items-center"><Eye className="w-6 h-6 mr-2" /> 正確答案</div><div className="text-4xl font-serif font-bold text-gray-900 mb-8">{String.fromCharCode(65 + currentQ.correctAnswerIndex)}</div><div className="bg-green-50 rounded-xl p-6 w-full text-left border border-green-100 max-h-[250px] overflow-y-auto"><h4 className="font-bold text-green-800 mb-2">解析：</h4><p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-serif">{currentQ.explanation}</p></div></div>)}
                        </div>
                        <div className="p-4 bg-gray-50 text-center text-xs text-gray-400 border-t border-gray-100 uppercase tracking-widest">{isDrawingMode ? (isPaperOpen ? '計算紙模式：請在下方格線區計算' : (currentTool === 'eraser' ? `橡皮擦模式：只會擦除 ${penColor} 顏色的筆跡` : '塗鴉模式：可直接在題目上註記')) : (isFlipped ? '點擊卡片隱藏答案' : '點擊卡片查看詳解')}</div>
                    </div>
                </div>
            </div>

            <div className="bg-white p-4 border-t border-gray-200 flex justify-center gap-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40 shrink-0">
                <button onClick={prevCard} disabled={currentIndex === 0} className="p-4 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30 transition-colors"><ArrowLeft className="w-6 h-6" /></button>
                <button onClick={() => setIsDrawingMode(!isDrawingMode)} className={`p-4 rounded-full transition-colors ${isDrawingMode ? 'bg-yellow-500 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}><PenTool className="w-6 h-6" /></button>
                <button onClick={() => setIsFlipped(!isFlipped)} disabled={isDrawingMode} className={`px-8 py-3 rounded-xl font-bold text-lg shadow-sm transition-all w-32 ${isFlipped ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-fudan-blue text-white hover:bg-blue-800 hover:shadow-md'} ${isDrawingMode ? 'opacity-50 cursor-not-allowed' : ''}`}>{isFlipped ? <span className="flex items-center justify-center"><EyeOff className="w-5 h-5" /></span> : <span className="flex items-center justify-center"><Eye className="w-5 h-5 mr-2" /> 翻看</span>}</button>
                <button onClick={nextCard} disabled={currentIndex === deck.length - 1} className="p-4 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30 transition-colors"><ArrowRight className="w-6 h-6" /></button>
            </div>
        </div>
    );
};
